// Context meter + summarize button — sits in the chat header.
// ONE measured number: the provider-reported prompt fill for the last completed
// turn (what the model actually processed), shown as a % of the model's full
// window — the same basis the agent's Context Budget block and the summarize
// trigger use. Two dimensions: measured fill (primary bar) + turn depth. Color
// journey green → amber → red; a passive context-rot marker; the Summarize
// control pulses at capacity but never acts on its own (passive by doctrine,
// user choice is priority #1).
import { Button } from '@onestone/ui/button';
import { chatStore } from '@/lib/chatStore';
import { effectiveContextWindow } from '@onestone/shared/models';
import { computeContextMeter, fmtTokens, type ContextMeterMeta, type MeterZone } from '@/lib/contextMeter';

interface ChatTokenMeterProps {
  conversationMeta: ContextMeterMeta;
  conversationId: string;
  isSummarizing: boolean;
  totalMessages: number;
  /** Active model id — drives the real context window. */
  model: string;
  /** Active provider — `subscription` is the CLI transport. */
  provider: string;
  /** Opted into the model's extended (1M) tier — only honored when transport supports it. */
  useExtendedContext: boolean;
}

const ZONE_TEXT: Record<MeterZone, string> = {
  green: 'text-emerald-600 dark:text-emerald-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};

const ZONE_FILL: Record<MeterZone, string> = {
  green: 'bg-emerald-500/70',
  amber: 'bg-amber-500/80',
  red: 'bg-red-500/85',
};

export function ChatTokenMeter({
  conversationMeta,
  conversationId,
  isSummarizing,
  totalMessages,
  model,
  provider,
  useExtendedContext,
}: ChatTokenMeterProps) {
  const transport = provider === 'subscription' ? 'subscription' : 'api';
  const contextWindow = effectiveContextWindow(model, provider, useExtendedContext, transport);
  const m = computeContextMeter(conversationMeta, contextWindow);
  if (!m.hasData) return null;

  const msgCount = m.messageCount > 0 ? m.messageCount : totalMessages;
  const textColor = ZONE_TEXT[m.zone];
  const fillLabel = m.fillPct.toFixed(0);

  const tooltip =
    `Context: ${fmtTokens(m.fillTokens)} / ${m.contextWindow.toLocaleString()} tokens (${fillLabel}%).` +
    `\nProvider-measured prompt for the last completed turn — what the model actually processed.` +
    `\n${msgCount} message${msgCount === 1 ? '' : 's'} — deeper threads degrade recall.` +
    `\nGreen → amber (70%) → red (85%). Marker = context-rot risk band.` +
    `\nSummarize is always your choice — nothing compacts on its own.`;

  return (
    <div data-testid="chat-token-meter" className="flex items-center gap-2">
      <div
        className="flex items-center gap-1.5"
        title={tooltip}
        aria-label={`Context ${fillLabel}% of window — ${fmtTokens(m.fillTokens)} measured tokens, ${msgCount} messages`}
      >
        <span className="text-xs font-medium tabular-nums text-foreground">{fmtTokens(m.fillTokens)}</span>
        <div className="relative h-1 w-12 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full transition-all ${ZONE_FILL[m.zone]}`}
            style={{ width: `${Math.max(2, m.fillPct)}%` }}
          />
          {/* Passive context-rot risk marker — a visible cue, never an action. */}
          <div
            className="absolute top-0 h-full w-px bg-foreground/30"
            style={{ left: `${m.rotBandPct}%` }}
            aria-hidden="true"
          />
        </div>
        <span className={`text-xs tabular-nums ${textColor}`}>{fillLabel}%</span>
        <span className="text-xs tabular-nums text-muted-foreground/70">· {msgCount}</span>
      </div>
      {conversationId.length > 0 ? (
        <Button
          variant="outline"
          size="sm"
          className={`h-5 px-1.5 text-xs ${textColor} ${m.atCapacity && !isSummarizing ? 'animate-pulse-subtle motion-reduce:animate-none' : ''}`}
          onClick={() => void chatStore.startSummarize()}
          disabled={isSummarizing}
          title="Checkpoint now — summarize the conversation so far. Every message stays here; the agent is sent the summary instead."
        >
          {isSummarizing ? 'Summarizing…' : 'Summarize'}
        </Button>
      ) : null}
    </div>
  );
}
